import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { RestaurantPublicDTO } from "../dto/restaurant.dto";

const RESTAURANT_CACHE_TTL = 60 * 1000; // 1 minuto

export const useRestaurantMetadata = (slug: string) => {
  const [data, setData] = useState<RestaurantPublicDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataRef = useRef<RestaurantPublicDTO | null>(null);
  dataRef.current = data;

  useEffect(() => {
    if (!slug) return;

    const cacheKey = `restaurant_cache_${slug}`;

    const readCache = (): { data: RestaurantPublicDTO; isFresh: boolean } | null => {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.data?.id) return null;
        const isFresh = Date.now() - (parsed.timestamp ?? 0) < RESTAURANT_CACHE_TTL;
        return { data: parsed.data, isFresh };
      } catch {
        localStorage.removeItem(cacheKey);
        return null;
      }
    };

    const writeCache = (value: RestaurantPublicDTO) => {
      localStorage.setItem(cacheKey, JSON.stringify({ data: value, timestamp: Date.now() }));
    };

    // force=true: omite isFresh y no muestra spinner (poll / focus)
    const fetchMetadata = async (force = false) => {
      setError(null);

      const cached = readCache();

      if (cached) {
        // Pinta cache de inmediato para render rápido
        if (dataRef.current?.updated_at !== cached.data.updated_at) setData(cached.data);
        setLoading(false);
        // SWR: aunque el cache sea fresco, siempre verifica la DB en background
        // (sin return) para detectar updated_at / last_published_at nuevos
        if (cached.isFresh && !force) {
          // continúa hacia el fetch de DB
        }
      } else if (!force) {
        setLoading(true);
      }

      try {
        const { data: res, error: rpcError } = await supabase.rpc(
          "get_restaurant_public_data",
          { p_slug: slug }
        );

        const raw = Array.isArray(res) ? res[0] : res;

        if (rpcError || !raw?.id) {
          if (!cached) {
            setError("Restaurante no encontrado");
            setData(null);
          }
          return;
        }

        writeCache(raw);
        const cur = dataRef.current;
        if (
          !cur ||
          raw.updated_at !== cur.updated_at ||
          raw.last_published_at !== cur.last_published_at
        ) {
          setData(raw);
        }
      } catch {
        if (!cached) {
          setError("Error al cargar los datos del restaurante");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();

    const onFocus = () => fetchMetadata(true);
    const onVisible = () => { if (document.visibilityState === "visible") fetchMetadata(true); };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [slug]);

  return { data, loading, error };
};
